import {CleanupJobs, Dispatchable, type ListenerHandle} from "../base/Dispatcher";
import {GlobalState, type RawStateData, type RawStoreObject, State, type StateData, type StoreEvent, type StoreFilter, type StoreId, type StoreIdOrNull, type StoreInterface, type StoreObjectType} from "./State";
import {isNotNull, isString, toArray} from "../base/Utils";
import {type FieldDescriptor, type StoreObjectWithFields} from "./StoreField";

// Another store, as the name it registered under or as the class itself
export type StoreDependency = StoreObjectType | StoreClass<StoreObject>;

export interface StoreOptions {
    state?: State;
    dependencies?: StoreDependency[]; // Other stores that should have their objects loaded before this
}

// Shorthand type for static method this parameter
export type StoreClass<T extends StoreObject> = {new (...args: any[]): T} & typeof StoreObject;

// An object's own store is its class, which ts-plugin/ names per subclass through the member below. It is read
// through a conditional rather than directly, so a class the plugin never reached (or a plain tsc, without it)
// falls back to the statics StoreClass carries instead of failing to index.
export type OwnStore<T extends StoreObject> =
    (T extends {$stemOwnStore?: infer StoreType} ? NonNullable<StoreType> : unknown) & StoreClass<T> & Dispatchable;

// A symbol to dispatch state events by type, since Dispatchable owns generic dispatchers
export const EventDispatcherSymbol = Symbol("EventDispatcher");

export class StoreObject extends Dispatchable {
    declare [EventDispatcherSymbol]?: Dispatchable;
    declare id: StoreId;

    // Every field on it is copied onto this, so any object shape is a legitimate thing to build from
    constructor(obj?: object, _event?: StoreEvent) {
        super();
        Object.assign(this, obj);
    }

    // Overloaded signatures for better typing
    // A nullish storeName resolves to the object's own store (same as the no-arg call)
    getStore(storeName?: null): OwnStore<this>;
    getStore<Name extends keyof StemStoreRegistry>(storeName: Name): StoreInterface<StemStoreRegistry[Name]> | undefined;
    getStore(storeName: string | null): StoreInterface | undefined;
    getStore(storeName?: string | null): OwnStore<this> | StoreInterface | undefined {
        const ownStore = this.constructor as OwnStore<this>;
        if (storeName) {
            return ownStore.getState().getStore(storeName);
        }
        return ownStore;
    }

    // By default, applying an event just shallow copies the fields from event.data
    applyEvent(event: StoreEvent): void {
        Object.assign(this, event.data);
    }

    applyEventAndDispatch(event: StoreEvent): void {
        this.applyEvent(event);
        this.dispatchChange(event);
    }

    addDeleteListener(callback: (event: StoreEvent, obj: this) => void): ListenerHandle {
        return this.addListener("delete", callback);
    }

    // Add a listener on updates from events with this specific type.
    // Can accept an array as eventType
    addEventListener(eventType: string | string[], callback: (event: StoreEvent, obj: this) => void): ListenerHandle {
        if (Array.isArray(eventType)) {
            const handlers = eventType.map(e => this.addEventListener(e, callback));
            return new CleanupJobs(handlers);
        }
        // Ensure the private event dispatcher exists
        if (!this[EventDispatcherSymbol]) {
            this[EventDispatcherSymbol] = new Dispatchable();
            this.addChangeListener((event: StoreEvent) => {
                if (event.type) {
                    this[EventDispatcherSymbol]!.dispatch(event.type, event, this);
                }
            });
        }
        return this[EventDispatcherSymbol]!.addListener(eventType, callback);
    }

    toJSON(): RawStoreObject {
        const obj: RawStoreObject = {};
        for (const key in this) {
            if (this.hasOwnProperty(key)) {
                obj[key] = this[key];
            }
        }
        return obj;
    }

// Static store logic
    // BaseStore copies Dispatchable's own properties and its prototype onto the store class, so the
    // dispatcher lives on the statics rather than on an instance
    declare static dispatch: Dispatchable["dispatch"];
    declare static dispatchChange: Dispatchable["dispatchChange"];
    declare static addListener: Dispatchable["addListener"];
    declare static addChangeListener: Dispatchable["addChangeListener"];

    static objectType: StoreObjectType;
    static state: State = GlobalState;
    static dependencies: StoreObjectType[] = [];
    // Not polymorphic - a static property is resolved where it is written. What a given store holds is said by
    // StoreClass<T>, which every static below takes as `this`. Naming the class here instead would cost an
    // emitted self-reference alias, so `typeof this` stays.
    static objects = new Map<string, InstanceType<typeof this>>();

    static makeFieldLoader<T extends StoreObject>(this: StoreClass<T>, fieldDescriptor: FieldDescriptor): (value: any, obj: StoreObjectWithFields) => T | undefined {
        fieldDescriptor.cacheField = false;
        fieldDescriptor.rawField = fieldDescriptor.rawField || (key => key + "Id");

        // A field into a store the owner declares a dependency on is guaranteed, since the state loader
        // imports dependencies first - the same rule getRequired states, decided here from the two object
        // types rather than from either class's name. `guaranteed` overrides it in both directions, and an
        // owner this cannot see keeps the plain lookup
        const owner = fieldDescriptor.targetProto?.constructor as typeof StoreObject | undefined;
        const guaranteed = fieldDescriptor.guaranteed ??
            owner?.dependencies?.some(dependency => dependency.toLowerCase() === this.objectType);

        // TODO resolve through the object's own store, once a state can own its stores instead of everything growing in the global one
        return (value: any, _obj: StoreObjectWithFields) => guaranteed ? this.getRequired(value) : this.get(value);
    }

    static loadRaw(responseOrState: StateData): RawStoreObject[] {
        const state = (responseOrState?.state || responseOrState || {}) as RawStateData;

        // Since the backend might have a different lettering case, need a more complex search here
        for (const [key, value] of Object.entries(state)) {
            if (String(key).toLowerCase() === this.objectType) {
                return toArray(value);
            }
        }

        return [];
    }

    // For a response/state raw object, return the objects that we have in store
    static load<T extends StoreObject>(this: StoreClass<T>, responseOrState: StateData): T[] {
        const rawObjects = this.loadRaw(responseOrState);
        return rawObjects.map(obj => this.get<T>(obj.id)).filter(isNotNull);
    }

    static loadObject<T extends StoreObject>(this: StoreClass<T>, responseOrState: StateData): T | undefined {
        return this.load<T>(responseOrState)?.[0];
    }

    static getState(): State {
        return this.state;
    }

    static get<T extends StoreObject>(this: StoreClass<T>, id: StoreIdOrNull): T | undefined {
        if (id == null) {
            return;
        }
        // The map is a static property, so it can't be polymorphic - StoreClass<T> is what says these are T
        return this.objects.get(String(id)) as T;
    }

    // A foreign key into one of the asking store's declared dependencies. importStateFromTempMap loads a
    // store's dependencies before the store itself, so the object is there by the time anything can ask.
    // Answering with T rather than T | undefined is what states that, and a miss says the state is wrong
    static getRequired<T extends StoreObject>(this: StoreClass<T>, id: StoreIdOrNull): T {
        const obj = this.get<T>(id);
        // Only a key that names something is a broken promise; a nullable foreign key with nothing in it
        // is simply absent, which get() already answers with nothing for
        if (!obj && id != null) {
            console.error("Missing", this.objectType, id, "- a declared dependency should have loaded it");
        }
        return obj;
    }

    static addObject<T extends StoreObject>(this: typeof StoreObject, id: StoreId, obj: T): void {
        this.objects.set(String(id), obj);
    }

    static clear(): void {
        this.objects.clear();
        this.dispatchChange();
    }

    static getObjectIdForEvent(event: StoreEvent): string {
        const id = event.objectId || event.data?.id;
        return String(id);
    }

    static getObjectForEvent<T extends StoreObject>(this: StoreClass<T>, event: StoreEvent): T | undefined {
        let objectId = this.getObjectIdForEvent(event);
        return this.get(objectId);
    }

    // The precise view of the objects map: a `this` parameter is the only thing that can carry the store's own
    // type, which the static property it wraps cannot
    static getObjects<T extends StoreObject>(this: StoreClass<T>): Map<string, T> {
        return this.objects as Map<string, T>;
    }

    static values<T extends StoreObject>(this: StoreClass<T>): IterableIterator<T> {
        return this.objects.values() as IterableIterator<T>;
    }

    // A store sorts its own type, which a static cannot name; the base is what every one of them is
    static comparator?: (a: StoreObject, b: StoreObject) => number;

    static all<T extends StoreObject>(this: StoreClass<T>): T[] {
        const values = Array.from(this.values());
        return this.comparator ? values.sort(this.comparator) : values;
    }

    static find<T extends StoreObject>(this: StoreClass<T>, callback: (value: T) => boolean): T | undefined {
        return this.all<T>().find(callback);
    }

    static filter<T extends StoreObject>(this: StoreClass<T>, callback: (value: T) => boolean): T[] {
        return this.all<T>().filter(callback);
    }

    // TODO Stores should have configurable indexes from FK ids, for quick filtering
    static filterBy<T extends StoreObject>(this: StoreClass<T>, filter: StoreFilter<T>): T[] {
        const entries = Object.entries(filter); // Some minimal caching

        return this.filter<T>((obj: T) => {
            for (const [key, value] of entries) {
                const objectValue = (obj as any)[key];
                // Can match by array (any value) or otherwise exact match
                if (Array.isArray(value)) {
                    if (!value.includes(objectValue)) {
                        return false;
                    }
                } else {
                    if (objectValue != value) {
                        return false;
                    }
                }
            }
            return true;
        });
    }

    static findBy<T extends StoreObject>(this: StoreClass<T>, filter: StoreFilter<T>): T | undefined {
        // TODO - need a better implementation with rapid termination
        return this.filterBy<T>(filter)[0];
    }

    static toJSON<T extends StoreObject>(this: StoreClass<T>): RawStoreObject[] {
        return this.all<T>().map((entry: T) => entry.toJSON());
    }

    static applyCreateOrUpdateEvent<T extends StoreObject>(this: StoreClass<T>, event: StoreEvent, sendDispatch: boolean = true): T {
        let obj = this.getObjectForEvent<T>(event);

        if (obj) {
            obj.applyEventAndDispatch(event);
        } else {
            obj = new this(event.data, event) as T;
            this.addObject<T>(this.getObjectIdForEvent(event), obj);
            if (sendDispatch) {
                this.dispatch("create", obj, event);
            }
        }
        if (sendDispatch) {
            this.dispatchChange(obj, event);
        }
        return obj;
    }

    static applyDeleteEvent<T extends StoreObject>(this: StoreClass<T>, event: StoreEvent): T | undefined {
        const obj = this.getObjectForEvent<T>(event);
        if (obj) {
            this.objects.delete(this.getObjectIdForEvent(event));
            obj.dispatch("delete", event, obj);
            this.dispatch("delete", obj, event);
            this.dispatch("change", obj, event);
        }
        return obj;
    }

    static applyEvent<T extends StoreObject>(this: StoreClass<T>, event: StoreEvent): T | undefined {
        event.data = event.data || {};

        if (event.type === "create" || event.type === "createOrUpdate") {
            return this.applyCreateOrUpdateEvent<T>(event);
        }

        if (event.type === "delete") {
            return this.applyDeleteEvent<T>(event);
        }

        // We're in the general case
        const obj = this.getObjectForEvent<T>(event);
        if (!obj) {
            console.error("Missing object of type ", this.objectType, " ", event.objectId);
            return undefined;
        }

        obj.applyEventAndDispatch(event);

        this.dispatchChange(obj, event); // TODO this is not a store event, but how can we still register for all of these?

        return obj;
    }

    static importState(objects: RawStoreObject[] = []): void {
        for (const obj of objects) {
            this.create(obj);
        }
    }

    static makeEventFromObject(obj: RawStoreObject | StoreObject, eventExtra: Record<string, unknown> | null = null): StoreEvent {
        return {
            isFake: true,
            type: "create",
            objectType: this.objectType,
            objectId: obj.id,
            data: obj,
            ...eventExtra,
        };
    }

    // Create a fake creation event, to insert the raw object
    static create<T extends StoreObject>(this: StoreClass<T>, obj: RawStoreObject | StoreObject, eventExtra: Record<string, unknown> | null = null, dispatchEvent: boolean = true): T | undefined {
        if (!obj) {
            return;
        }

        const event = this.makeEventFromObject(obj, eventExtra);
        return this.applyCreateOrUpdateEvent<T>(event, dispatchEvent);
    }

    // Add a listener on all object creation events
    // If fakeExisting, will also pass existing objects to your callback
    static addCreateListener<T extends StoreObject>(this: StoreClass<T>, callback: (obj: T, event: StoreEvent) => void, fakeExisting?: boolean) {
        if (fakeExisting) {
            for (const obj of this.values()) {
                const event = this.makeEventFromObject(obj);
                callback(obj, event);
            }
        }

        return this.addListener("create", callback);
    }

    // Add a listener for any object deletions
    static addDeleteListener<T extends StoreObject>(this: StoreClass<T>, callback: (obj: T, event: StoreEvent) => void) {
        return this.addListener("delete", callback);
    }
}

export function globalStore<T extends StoreInterface & (new (...args: any[]) => object)>(constructor: T): T {
    // Register the store with GlobalState immediately
    GlobalState.addStore(constructor);
    return constructor;
}

// Generic over the base class itself, not just its instance type, so a base store's statics survive into the subclass
export function BaseStore<BaseType extends StoreClass<StoreObject> = typeof StoreObject>(objectType: string, options: StoreOptions = {}, BaseClass?: BaseType): BaseType & Dispatchable {
    const state = options.state || GlobalState;
    BaseClass = BaseClass || (StoreObject as BaseType);
    const dependencies = [...(options.dependencies || []), ...(BaseClass.dependencies || [])];

    // A mixin base has to be a single rest-args signature, which the constraint isn't - it also carries the
    // statics. Those reach the caller through the return type, so the base only has to construct here.
    class Store extends (BaseClass as new (...args: any[]) => StoreObject) {
        static objectType = objectType.toLowerCase();
        static state = state;
        static dependencies = dependencies.map(d => isString(d) ? d : d.objectType);
        static objects = new Map<string, InstanceType<BaseType>>();
    }

    // Copy Dispatchable instance properties and methods to the Store class
    const dispatchableInstance = new Dispatchable();
    Object.assign(Store, dispatchableInstance);

    // Copy Dispatchable prototype methods and getters/setters to Store
    const dispatchableProto = Dispatchable.prototype;
    Object.getOwnPropertyNames(dispatchableProto).forEach(name => {
        if (name === "constructor") {
            return;
        }
        const descriptor = Object.getOwnPropertyDescriptor(dispatchableProto, name);
        if (descriptor) {
            Object.defineProperty(Store, name, descriptor);
        }
    });

    return Store as any;
}
